/**
 * Harness HTTP 客户端：负责配置、柜柜 Token 兑换、Agent Token 缓存和受保护请求。
 * 其他模块只能通过公开方法发起 Harness 请求，不应自行拼接鉴权 Header。
 */
import type {
  GuiguiAgentToken,
  HarnessEnvelope,
  HarnessFetchInit,
} from './types'

const HARNESS_API_BASE_PATH = '/api/v1/agent'
const GUIGUI_EXCHANGE_PATH = '/santi/access_token'
const GUIGUI_TOKEN_STORAGE_KEY = 'token'
const AGENT_TOKEN_STORAGE_KEY = 'st_agent_token'

export function isAssistantChatConfigured(): boolean {
  return Boolean(process.env.VUE_APP_ASSISTANT_API_BASE_URL?.trim() || '')
}

export class HarnessApiError extends Error {
  readonly status: number
  readonly code?: string

  constructor(status: number, message: string, code?: string) {
    super(message)
    this.name = 'HarnessApiError'
    this.status = status
    this.code = code
  }
}

const harnessGlobals = globalThis as unknown as {
  crypto?: { randomUUID?: () => string }
}

let harnessRequestSequence = 0

export function createHarnessRequestId(): string {
  const randomUUID = harnessGlobals.crypto?.randomUUID
  if (randomUUID) {
    return randomUUID.call(harnessGlobals.crypto)
  }

  harnessRequestSequence += 1
  const random = Math.random().toString(36).slice(2, 10)
  return `req_${Date.now().toString(36)}${harnessRequestSequence.toString(
    36
  )}${random}`
}

let activeGuiguiExchange: Promise<string> | null = null

// 同一时刻只允许一次凭据兑换，避免并发请求重复消耗柜柜登录 Token。

export function getHarnessAuthorization(
  fetchImpl?: typeof fetch
): Promise<string> {
  const storedAccessToken = readHarnessSessionValue(AGENT_TOKEN_STORAGE_KEY)
  if (storedAccessToken) {
    return Promise.resolve(storedAccessToken)
  }

  if (!activeGuiguiExchange) {
    activeGuiguiExchange = exchangeGuiguiToken(fetchImpl).then(
      (accessToken) => {
        activeGuiguiExchange = null
        return accessToken
      },
      (error) => {
        activeGuiguiExchange = null
        throw error
      }
    )
  }

  return activeGuiguiExchange
}

export async function harnessFetch(
  path: string,
  init: HarnessFetchInit = {}
): Promise<Response> {
  const fetchImpl = resolveFetch(init.fetchImpl)
  const url = getHarnessUrl(path)
  const serializedBody =
    init.body === undefined ? undefined : JSON.stringify(init.body)
  const createRequestInit = (accessToken: string): RequestInit => {
    const requestInit: RequestInit = {
      method: init.method || (serializedBody === undefined ? 'GET' : 'POST'),
      headers: {
        ...init.headers,
        Authorization: `Bearer ${accessToken}`,
      },
    }
    if (serializedBody !== undefined) {
      requestInit.body = serializedBody
    }
    if (init.signal) {
      requestInit.signal = init.signal
    }
    return requestInit
  }

  const authorization = await getHarnessAuthorization(fetchImpl)
  let response = await fetchImpl(url, createRequestInit(authorization))

  // 401 仅刷新一次，并复用同一序列化请求体，保证请求身份和幂等键不变化。
  if (response.status === 401) {
    if (readHarnessSessionValue(AGENT_TOKEN_STORAGE_KEY) === authorization) {
      clearHarnessSessionValue(AGENT_TOKEN_STORAGE_KEY)
    }
    await discardResponseBody(response)
    response = await fetchImpl(
      url,
      createRequestInit(await getHarnessAuthorization(fetchImpl))
    )
  }

  if (!response.ok) {
    const failure = await readHarnessEnvelope<unknown>(response)
    throw createHarnessApiError(response.status, failure.envelope, failure.raw)
  }

  return response
}

async function exchangeGuiguiToken(fetchImpl?: typeof fetch): Promise<string> {
  const guiguiToken = readHarnessSessionValue(GUIGUI_TOKEN_STORAGE_KEY)
  if (!guiguiToken) {
    throw new HarnessApiError(
      401,
      '登录状态已失效，请重新登录后再使用助手',
      'GUIGUI_TOKEN_MISSING'
    )
  }

  const fetchRequest = resolveFetch(fetchImpl)
  const response = await fetchRequest(
    getGuiguiExchangeUrl(GUIGUI_EXCHANGE_PATH),
    {
      method: 'POST',
      headers: { token: guiguiToken },
    }
  )
  const failure = await readHarnessEnvelope<GuiguiAgentToken>(response)
  if (!response.ok || failure.envelope?.success === false) {
    throw createHarnessApiError(response.status, failure.envelope, failure.raw)
  }

  const accessToken = failure.envelope?.data?.access_token?.trim()
  if (!accessToken) {
    throw new HarnessApiError(
      response.status,
      'Agent 登录凭据兑换失败',
      'AGENT_TOKEN_MISSING'
    )
  }

  writeHarnessSessionValue(AGENT_TOKEN_STORAGE_KEY, accessToken)
  return accessToken
}

function getHarnessUrl(path: string): string {
  const baseUrl = process.env.VUE_APP_ASSISTANT_API_BASE_URL?.trim() || ''
  if (!baseUrl) {
    throw new HarnessApiError(
      0,
      '本地助手服务未配置',
      'ASSISTANT_API_NOT_CONFIGURED'
    )
  }

  return `${baseUrl.replace(/\/$/, '')}${HARNESS_API_BASE_PATH}${path}`
}

export function getGuiguiExchangeUrl(path: string): string {
  const baseUrl = process.env.VUE_APP_BASE_URL?.trim() || ''
  if (!baseUrl) {
    throw new HarnessApiError(
      0,
      '柜柜服务地址未配置',
      'GUIGUI_API_NOT_CONFIGURED'
    )
  }

  return `${baseUrl.replace(/\/$/, '')}${path}`
}

function resolveFetch(fetchImpl?: typeof fetch): typeof fetch {
  const fetchRequest = fetchImpl || globalThis.fetch
  if (!fetchRequest) {
    throw new HarnessApiError(
      0,
      '当前环境不支持 fetch 请求',
      'FETCH_UNAVAILABLE'
    )
  }

  return fetchRequest
}

async function discardResponseBody(response: Response): Promise<void> {
  const body = response.body
  if (!body) {
    return
  }

  try {
    await body.cancel()
  } catch {
    // An uncancellable body must never block the 401 retry.
  }
}

export async function readHarnessEnvelope<T>(
  response: Response
): Promise<{ envelope?: HarnessEnvelope<T>; raw: string }> {
  let raw = ''
  try {
    raw = await response.text()
  } catch {
    raw = ''
  }

  const trimmed = raw.trim()
  if (!trimmed) {
    return { raw }
  }

  try {
    return { envelope: JSON.parse(trimmed) as HarnessEnvelope<T>, raw }
  } catch {
    return { raw }
  }
}

export function createHarnessApiError<T>(
  status: number,
  envelope: HarnessEnvelope<T> | undefined,
  raw: string
): HarnessApiError {
  const code = firstMeaningfulValue(envelope?.errorCode) || undefined
  const documentedMessage = firstMeaningfulValue(
    envelope?.detail,
    envelope?.message,
    envelope?.error,
    code
  )
  const rawMessage = raw.trim()
  let fallbackMessage = `Harness 请求失败：HTTP ${status}`
  if (envelope === undefined && rawMessage) {
    fallbackMessage =
      rawMessage.length > 300 ? `${rawMessage.slice(0, 300)}…` : rawMessage
  }

  return new HarnessApiError(status, documentedMessage || fallbackMessage, code)
}

function firstMeaningfulValue(...values: unknown[]): string {
  for (const value of values) {
    if (typeof value !== 'string') {
      continue
    }

    const trimmed = value.trim()
    if (trimmed) {
      return trimmed
    }
  }

  return ''
}

function getHarnessSessionStorage(): Storage | null {
  try {
    return typeof sessionStorage === 'undefined' ? null : sessionStorage
  } catch {
    return null
  }
}

function readHarnessSessionValue(key: string): string {
  const storage = getHarnessSessionStorage()
  const value = storage ? storage.getItem(key) : null
  return value ? value.trim() : ''
}

function writeHarnessSessionValue(key: string, value: string): void {
  const storage = getHarnessSessionStorage()
  if (storage) {
    storage.setItem(key, value)
  }
}

function clearHarnessSessionValue(key: string): void {
  const storage = getHarnessSessionStorage()
  if (storage) {
    storage.removeItem(key)
  }
}

export function createHarnessQuery(
  values: Record<string, string | number | boolean | undefined>
): string {
  const params = new URLSearchParams()
  Object.keys(values).forEach((key) => {
    const value = values[key]
    if (value !== undefined && value !== '') {
      params.set(key, String(value))
    }
  })
  const query = params.toString()
  return query ? `?${query}` : ''
}

export function asNonEmptyString(value: unknown): string {
  return typeof value === 'string' ? value.trim() : ''
}

export function isPlainObject(
  value: unknown
): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
}
